import { SectionHeading } from '@/components/ui/SectionHeading';
import { AnimateIn } from '@/components/ui/AnimateIn';
import { HOW_IT_WORKS_STEPS, type HowItWorksStep } from '@/data/content';

function StepIcon({ icon }: { icon: HowItWorksStep['icon'] }) {
  const size = 28;
  const shared = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (icon) {
    case 'bell':
      return (
        <svg {...shared}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    case 'running':
      return (
        <svg {...shared}>
          <circle cx="17" cy="4" r="2" />
          <path d="M15.59 13.51l1.39 2.41a1 1 0 0 0 1.36.37l2.16-1.23" />
          <path d="M7.95 17.57L5 21" />
          <path d="M13.21 9l-2.66 2.66a1 1 0 0 0 0 1.41l.71.71a1 1 0 0 0 1.41 0L15.32 11" />
          <path d="M10 5l-1.68 3.36a2 2 0 0 0 .28 2.22L12 14" />
          <path d="M12 14l-2 5" />
        </svg>
      );
    case 'scan':
      return (
        <svg {...shared}>
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <line x1="7" y1="12" x2="17" y2="12" />
        </svg>
      );
    case 'piggy':
      return (
        <svg {...shared}>
          <path d="M12 2v4" />
          <path d="M6 6h12l1 10H5L6 6z" />
          <path d="M5 16l-1 4h2l1-2h10l1 2h2l-1-4" />
          <circle cx="9" cy="10" r="1" fill="currentColor" />
          <circle cx="15" cy="10" r="1" fill="currentColor" />
        </svg>
      );
  }
}

export function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-white py-24">
      <div className="container mx-auto px-6">
        <SectionHeading
          eyebrow="How It Works"
          title="From notification to savings in minutes"
        />

        <div className="relative mt-16">
          {/* Connector line (desktop: horizontal, mobile: vertical) */}
          <div className="absolute top-12 right-12 left-12 hidden h-px border-t-2 border-dashed border-brand-200 md:block" />
          <div className="absolute top-0 bottom-0 left-8 w-px border-l-2 border-dashed border-brand-200 md:hidden" />

          <div className="grid gap-12 md:grid-cols-4 md:gap-8">
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <AnimateIn key={step.number} delay={index * 100}>
                <div className="relative flex gap-6 md:flex-col md:items-center md:text-center md:gap-4">
                  {/* Number circle */}
                  <div className="relative z-10 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg">
                    <span className="text-lg font-bold">{step.number}</span>
                  </div>

                  <div>
                    {/* Icon */}
                    <div className="mb-2 text-brand-500 md:mt-4">
                      <StepIcon icon={step.icon} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
